<template>
  <div class="content max-w-screen-3xl">
    <h1>Candles</h1>
    <div class="flex flex-col gap-4">
      <div>
        <div class="flex justify-between gap-4">
          <div class="flex gap-4 items-center w-full">
            <b-btn
              size="sm"
              variant="white"
              @click="openFilters"
            >
              <div class="flex gap-2 items-center">
                <i class="fas fa-sliders-h"></i>
                <span>Filters</span>
              </div>
            </b-btn>

            <div class="flex gap-2 items-center w-full">
              <div>
                <b-form-select
                  v-model="filters.candle_status"
                  :options="candleOptions.candle_status"
                  size="sm"
                  style="width: 130px !important;"
                  @input="scheduleFetching"
                >
                  <template #first>
                    <b-form-select-option :value="null" disabled>
                      Status
                    </b-form-select-option>
                  </template>
                </b-form-select>
              </div>

              <div>
                <v-select
                  v-model="filters.brand_id"
                  :options="dropdowns.candle_brand"
                  size="sm"
                  label="text"
                  :reduce="option => option.value"
                  appendToBody
                  :calculatePosition="calculatePosition"
                  placeholder="Brand"
                  style="width: 180px !important;"
                  @input="scheduleFetching"
                />
              </div>

              <div>
                <v-select
                  v-model="filters.glass_id"
                  :options="dropdowns.glass"
                  size="sm"
                  label="text"
                  :reduce="option => option.value"
                  class="w-100"
                  appendToBody
                  :calculatePosition="calculatePosition"
                  placeholder="Glass"
                  style="width: 140px !important;"
                  @input="scheduleFetching"
                />
              </div>

              <div>
                <v-select
                  v-model="filters.fragrance_name_id"
                  :options="dropdowns.fragrance"
                  size="sm"
                  label="text"
                  :reduce="option => option.value"
                  appendToBody
                  :calculatePosition="calculatePosition"
                  placeholder="Fragrance"
                  style="width: 250px !important;"
                  @input="scheduleFetching"
                />
              </div>

              <b-form-input
                v-model="filters.search"
                size="sm"
                class="w-full max-w-sm"
                autocomplete="off"
                @input="scheduleFetching"
                placeholder="Search by text"
                type="search"
              ></b-form-input>
            </div>
          </div>

          <div class="flex gap-2 items-center">
            <b-btn
              size="sm"
              variant="white"
              @click="openRecipe"
              class="text-nowrap"
            >
              Print Recipe
            </b-btn>

            <b-btn
              size="sm"
              variant="primary"
              @click="createCandle"
            >
              New
            </b-btn>
          </div>

        </div>
      </div>

      <div>
        <active-filters v-model="filters" @refresh="fetchRecords()" />
      </div>

      <div class="font-bold" v-if="candles">
        Showing {{ candles.length | format }} / {{ recordCount | format }} candle(s)
      </div>

      <template v-if="candles">
        <desktop-table
          :candles="candles"
          :filters="filters"
          class="hidden lg:table"
        />
        <mobile-table
          :candles="candles"
          :filters="filters"
          class="lg:hidden"
        />
      </template>
    </div>

    <b-pagination
      v-model="filters.page"
      :total-rows="recordCount"
      :per-page="filters.per_page"
      @change="paginate"
      :disabled="isLoading"
      class="mt-4"
    ></b-pagination>

    <filter-modal
      :data="filterModal"
      :filters="filters"
      :default-filters="defaultFilters"
      @input="scheduleFetching"
    />

    <create-modal
      :data="createModal"
      @created="onCandleCreated"
    />

    <recipe-modal
      v-if="recipeModal.showModal"
      :data="recipeModal"
    />
  </div>
</template>

<script>
import {
  calculateDropdownPosition,
  decodeFilters,
  encodeFilters,
} from '@/helpers';
import {
  candleOptions,
  defaultFilters,
} from '@/composables/candle';

const _ = require('lodash');

const FilterModal = () => import('@/components/candles/FilterModal.vue');
const ActiveFilters = () => import('@/components/candles/ActiveFilters.vue');
const CreateModal = () => import('@/components/candle/CreateModal.vue');
const DesktopTable = () => import('@/components/candles/DesktopTable.vue');
const MobileTable = () => import('@/components/candles/MobileTable.vue');
const RecipeModal = () => import('@/components/candles/RecipeModal.vue');

export default {
  name: 'Candles',
  components: {
    FilterModal,
    ActiveFilters,
    CreateModal,
    DesktopTable,
    MobileTable,
    RecipeModal,
  },
  computed: {
    isLoading() {
      return this.loadingCount > 0;
    },
    dropdowns() {
      return this.$store.state.app.dropdowns;
    },
    defaultFilters() {
      return defaultFilters;
    },
    candleOptions() {
      return candleOptions;
    },
  },
  data() {
    return {
      loadingCount: 0,
      candles: null,
      recordCount: 0,
      scheduleFetching: _.debounce(() => {
        this.fetchRecords();
      }, 500),
      filterModal: {
        showModal: false,
      },
      createModal: {
        showModal: false,
      },
      recipeModal: {
        showModal: false,
        candleIds: [],
        candleCount: 1,
      },
      filters: {
        date_from: null,
        date_to: null,
        date_range: 'all',
        page: 1,
        per_page: 50,
        search: '',
        brand_id: null,
        glass_id: null,
        wax_id: null,
        wick_id: null,
        wick_thickness_id: null,
        wick_count_id: null,
        dye_name_id: null,
        fragrance_name_id: null,
        additive_name_id: null,
        dye_pct_id: null,
        fragrance_pct_id: null,
        additive_pct_id: null,
        candle_status: null,
        is_favorite: null,
      },
    };
  },
  methods: {
    createCandle() {
      this.createModal.showModal = true;
    },
    onCandleCreated(candle, viewCandle) {
      this.candles.unshift(candle);
      this.recordCount++;
      if (viewCandle) {
        this.viewCandle(candle);
      }
    },
    viewCandle(candle) {
      this.$router.push({
        path: `/candles/${candle.id}`,
        query: {
          filters: encodeFilters(this.filters, defaultFilters),
        },
      }, () => {});
    },
    paginate() {
      this.$nextTick().then(() => {
        this.fetchRecords(true);
      });
    },
    openFilters() {
      this.filterModal.showModal = true;
    },
    openRecipe() {
      this.recipeModal.showModal = true;
      this.recipeModal.candleIds = this.candles.map((candle) => candle.id);
    },
    fetchRecordData() {
      this.loadingCount++;
      this.$http
        .get('/candle')
        .query(this.filters)
        .then((res) => {
          this.candles = res.body;
        })
        .catch((err) => {
          this.$toast.error(`Failed to fetch candles: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    fetchRecordCount() {
      this.loadingCount++;
      this.$http
        .get('/candle_count')
        .query(this.filters)
        .then((res) => {
          this.recordCount = res.body;
        })
        .catch((err) => {
          this.$toast.error(`Failed to fetch candle count: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    fetchRecords(paginate) {
      paginate = paginate === undefined ? false : paginate;
      if (paginate === false) {
        this.fetchRecordCount();
      } else {
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 50);
      }
      this.fetchRecordData();
      this.setQuery();
    },
    fetchAll() {
      this.fetchRecords();
    },
    setQuery() {
      const encodedFilters = encodeFilters(this.filters, defaultFilters);
      this.$router.replace({
        query: {
          filters: encodedFilters,
        },
      }, () => {});
    },
    getQuery() {
      const urlFilters = this.$route.query.filters;
      if (urlFilters) {
        const decodedFilters = decodeFilters(urlFilters, defaultFilters);
        this.filters = decodedFilters;
      }
    },
    calculatePosition(dropdownList, component) {
      return calculateDropdownPosition(dropdownList, component);
    },
  },
  created() {
    this.getQuery();
    this.fetchAll();
  },
};
</script>

<style lang="scss" scoped>
</style>
